import {useCallback, useEffect, useRef, useState} from "react";
import fileToMetadata, {FileMetadata, TimeSource} from "../../../../utils/files/fileToDate";
import {useInterval} from "@mantine/hooks";
import { v4 as uuidv4 } from 'uuid';
import heic2any from "heic2any";
import Compressor from "compressorjs"
import axios from "axios";
import config from "../../../../config";
import useAuth from "../../../../utils/auth";
import {compressVideo, isCompressVideoSupported} from "../../../../utils/files/compressVideo";

export enum FileStatus {
  "idle" = "idle",
  "heic" = "heic",
  "compression" = "compression",
  "videoCompression" = "videoCompression",
  "wainingForUpload" = "wainingForUpload",
  "uploading" = "uploading",
  "done" = "done",
  "failed" = "failed"
}

const VIDEO_TARGET = {
  maxWidth: 1920,
  maxHeight: 1080,
  videoBitsPerSecond: 8_000_000,
  audioBitsPerSecond: 128_000,
  frameRate: 30
}


interface FileInQueueState {
  key: string
  date: string
  timeSource: TimeSource
  latitude: number | null
  longitude: number | null
  status: FileStatus
  progress: number
  videoCompressionProgress?: number
  name: string
  url: string
  compression?: number
  compressionSize?: number
  size: number
  isVideo: boolean
}

interface FileInQueue extends FileInQueueState {
  files: {
    original: File
    file: File | Blob
  }
}

const isRunningCompressStatus = [FileStatus.heic, FileStatus.compression, FileStatus.videoCompression, FileStatus.uploading]
const isRunningUploadStatus = [FileStatus.uploading]
const isRunningStatus = [...isRunningCompressStatus, ...isRunningUploadStatus]

const compressLimit = 10
const videoCompressLimit = 1
const limitUpload = 5

const VIDEO_EXTENSIONS = ["mp4", "mov", "m4v", "webm", "mkv", "avi", "3gp", "3gpp", "hevc"]

const isVideoFile = (file: File): boolean => {
  if (file.type && file.type.toLowerCase().startsWith("video/")) return true
  const ext = file.name.split(".").pop()?.toLowerCase() ?? ""
  return VIDEO_EXTENSIONS.includes(ext)
}

const useUploader = (galleryId: string) => {
  const auth = useAuth()
  const queueRef = useRef<{[key: string]: FileInQueue}>({})

  const addToQueue = useCallback(async (files: File[]) => {
    for (const file of files) {
      const key = uuidv4()
      const meta: FileMetadata = await fileToMetadata(file)
      const video = isVideoFile(file)

      queueRef.current[key] = {
        key,
        date: meta.timestamp.toString(),
        timeSource: meta.timeSource,
        latitude: meta.latitude,
        longitude: meta.longitude,
        status: FileStatus.idle,
        progress: 0,
        name: file.name,
        url: video ? "" : URL.createObjectURL(file),
        size: file.size,
        isVideo: video,
        files: {
          original: file,
          file
        }
      }
    }
  }, [])

  const reset = useCallback((key: string) => {
    queueRef.current[key].status = FileStatus.idle
  }, [])


  const [queue, setQueue] = useState<FileInQueueState[]>([])

  const interval = useInterval(() => {
    //Status for frontend
    const newQueue = Object.values(queueRef.current).map(item => {
      return {...item, files: null}
    })

    setQueue(newQueue)

    //Uploading logic
    const waitingCompress = Object.values(queueRef.current).filter(item => item.status === FileStatus.idle)
    const runningCompress = Object.values(queueRef.current).filter(item => isRunningCompressStatus.includes(item.status)).length
    const runningVideoCompress = Object.values(queueRef.current).filter(item => item.status === FileStatus.videoCompression).length

    if(waitingCompress.length > 0 && runningCompress < compressLimit) {
      let next = waitingCompress.find(item => !item.isVideo)
      if(!next && runningVideoCompress < videoCompressLimit) {
        next = waitingCompress.find(item => item.isVideo)
      }
      if(next) processFile(next.key)
    }

    const waitingUpload = Object.values(queueRef.current).filter(item => item.status === FileStatus.wainingForUpload)
    const runningUpload = Object.values(queueRef.current).filter(item => isRunningUploadStatus.includes(item.status)).length

    if(waitingUpload.length > 0 && runningUpload < limitUpload) {
      sendToServer(waitingUpload[0].key)
    }
  }, 300)

  const processFile = (fileId: string) => {
    const file = queueRef.current[fileId]

    if (file.isVideo) {
      compressVideoFile(fileId)
      return
    }

    if(file.files.file.type === "image/heic") {
      queueRef.current[file.key].status = FileStatus.heic

      heic2any({
        blob: file.files.file,
        toType: "image/jpeg",
        quality: 1
      }).then(jpeg => {
        //@ts-ignore
        queueRef.current[fileId].files.file = jpeg
        compressFile(fileId)
      })
    } else {
      compressFile(fileId)
    }
  }

  const compressVideoFile = async (fileId: string) => {
    const item = queueRef.current[fileId]
    if (!item) return

    if (!isCompressVideoSupported()) {
      prepareToSend(fileId)
      return
    }

    queueRef.current[fileId].status = FileStatus.videoCompression
    queueRef.current[fileId].videoCompressionProgress = 0

    try {
      const original = item.files.original
      const result = await compressVideo(item.files.file, {
        ...VIDEO_TARGET,
        onProgress: pct => {
          if (queueRef.current[fileId]) queueRef.current[fileId].videoCompressionProgress = pct
        }
      })

      if (!queueRef.current[fileId]) return

      // If the source had audio but the compressed output lost it, discard the
      // compressed result and upload the original rather than silently dropping sound.
      const droppedAudio = result.sourceHadAudio && !result.audioAttached
      if (droppedAudio) {
        console.warn("Compressed video lost its audio track, uploading original")
      }

      if (!droppedAudio && result.blob.size < original.size) {
        const baseName = original.name.replace(/\.[^/.]+$/, "")
        const newName = `${baseName}.${result.extension}`
        queueRef.current[fileId].files.file = new File([result.blob], newName, {type: result.mimeType})
        queueRef.current[fileId].name = newName
        queueRef.current[fileId].compression = Math.round(100 - (result.blob.size / original.size * 100))
        queueRef.current[fileId].compressionSize = result.blob.size
      }

      prepareToSend(fileId)
    } catch (err) {
      console.warn("Video compression failed, uploading original", err)
      if (queueRef.current[fileId]) {
        prepareToSend(fileId)
      }
    }
  }

  const compressFile = (fileId: string) => {
    const file = queueRef.current[fileId]

    queueRef.current[file.key].status = FileStatus.compression

    new Compressor(file.files.file, {
      quality: 0.9,
      success(result) {
        const percent = Math.round(100 - (result.size / file.files.original.size * 100))

        queueRef.current[fileId].files.file = result
        queueRef.current[fileId].compression = percent
        queueRef.current[fileId].compressionSize = result.size

        prepareToSend(fileId)
      },
      error(error) {
        prepareToSend(fileId)
      }
    })
  }

  const prepareToSend = (fileId: string) => {
    const file = queueRef.current[fileId]
    queueRef.current[file.key].status = FileStatus.wainingForUpload
  }

  const sendToServer = (fileId: string) => {
    const file = queueRef.current[fileId]

    queueRef.current[file.key].status = FileStatus.uploading
    queueRef.current[file.key].progress = 0

    const formData = new FormData()
    const mimeType = file.files.file.type || file.files.original.type || "application/octet-stream"
    const attachement = new File([file.files.file], file.name, {type: mimeType})
    formData.append("file", attachement);
    formData.append("lastModified", file.date);
    formData.append("time_source", file.timeSource);
    if (Number.isFinite(file.latitude)) formData.append("latitude", (file.latitude as number).toString());
    if (Number.isFinite(file.longitude)) formData.append("longitude", (file.longitude as number).toString());

    axios.post(`${config.apiURL}/api/galleries/${galleryId}/upload`, formData, {
      onUploadProgress: (progressEvent => {
        // @ts-ignore
        const progress = Math.round(progressEvent.loaded / progressEvent.total * 100)
        queueRef.current[file.key].progress = progress
      }),
      headers: {'Authorization': `Bearer ${auth.apiKey}`}
    }).then(() => {
      queueRef.current[file.key].status = FileStatus.done
      queueRef.current[file.key].progress = 100
    }).catch((err) => {
      console.error("Upload failed", file.name, err?.response?.status, err?.response?.data ?? err?.message)
      queueRef.current[file.key].status = FileStatus.failed
    })
  }

  useEffect(() => {
    interval.start();
    return interval.stop;
  }, []);

  return {
    queue,
    addToQueue,
    reset,
    stats: {
      waiting: queue.filter(item => item.status === FileStatus.idle).length,
      processing: queue.filter(item => isRunningStatus.includes(item.status)).length,
      failed: queue.filter(item => item.status === FileStatus.failed).length,
      done: queue.filter(item => item.status === FileStatus.done).length,
      total: queue.length
    }
  }
}

export default useUploader
